"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import FolderClip from "./folder-clip";

const tabs = [
  { id: "ai-training", label: "AI Training" },
  { id: "market-research", label: "Market Research" },
  { id: "user-research", label: "User Research" },
] as const;

type TabId = (typeof tabs)[number]["id"];

interface HeroFolderTabsProps {
  className?: string;
}

export default function HeroFolderTabs({ className }: HeroFolderTabsProps) {
  const [activeTab, setActiveTab] = useState<TabId>("ai-training");
  const [isPaused, setIsPaused] = useState(false);

  const activeIndex = tabs.findIndex((t) => t.id === activeTab);

  const goToNext = useCallback(() => {
    setActiveTab((current) => {
      const currentIndex = tabs.findIndex((t) => t.id === current);
      const nextIndex = (currentIndex + 1) % tabs.length;
      return tabs[nextIndex]!.id;
    });
  }, []);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    if (isPaused) {
      return;
    }

    const interval = setInterval(goToNext, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [isPaused, goToNext]);

  return (
    <div
      className={cn("mx-auto hidden w-full max-w-[740px] md:block", className)}
      style={{
        filter: "drop-shadow(4px 9px 20px rgba(0, 6, 183, 0.25))",
      }}
      onMouseEnter={() => {
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        setIsPaused(false);
      }}
    >
      {/* Tabs row - folder tabs with trapezoid shape */}
      <div className="relative flex items-end justify-center gap-5 px-[60px]">
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={cn(
                "relative h-[39px] w-[195px] transition-colors duration-300",
                isActive
                  ? "z-10"
                  : index === 0
                    ? "z-[3]"
                    : index === 1
                      ? "z-[2]"
                      : "z-[1]",
              )}
            >
              {/* Tab shape from Figma */}
              <svg
                preserveAspectRatio="none"
                width="100%"
                height="100%"
                overflow="visible"
                viewBox="0 0 195 39"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute inset-0"
              >
                <path
                  d="M11.9272 11.9356C15.0827 4.68735 22.2356 0 30.1409 0H165.065C172.97 0 180.123 4.68736 183.279 11.9356L195.206 39.3325H0L11.9272 11.9356Z"
                  fill={
                    isActive ? "#FFFFF0" : index === 1 ? "#3561FF" : "#4D6FFF"
                  }
                />
              </svg>
              {/* Tab label */}
              <span
                className={cn(
                  "absolute inset-0 flex items-center justify-center pt-1 font-mono text-[13px] tracking-[-0.26px]",
                  isActive ? "text-marketing-blue" : "text-marketing-cream",
                )}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Folder content - cream background with rounded corners */}
      <div className="border-marketing-border bg-marketing-cream-light relative overflow-visible rounded-[23px] border">
        {/* Inner card with inset shadow */}
        <div
          className="border-marketing-border-light bg-marketing-cream m-[6px] overflow-visible rounded-[20px] border"
          style={{
            boxShadow: "inset 1.5px -1.5px 3px rgba(96, 96, 96, 0.25)",
          }}
        >
          <TabContent tabId={activeTab} />
        </div>

        {/* Pagination dots */}
        <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-[7px]">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={cn(
                "h-2 rounded-full transition-[width,background-color] duration-300",
                activeIndex === index
                  ? "bg-marketing-border w-7"
                  : "bg-marketing-border-light hover:bg-marketing-border w-2",
              )}
              aria-label={`Go to ${tab.label}`}
            />
          ))}
        </div>

        {/* Folder clip - positioned on the right edge, above everything */}
        <div
          className="pointer-events-none absolute z-50"
          style={{
            right: -38,
            top: 45,
          }}
        >
          <FolderClip width={106} height={77} className="rotate-90" />
        </div>
      </div>
    </div>
  );
}

// Screener content per tab
const tabScreeners: Record<
  TabId,
  { screeners: { value: string; primary?: boolean }[] }
> = {
  "user-research": {
    screeners: [
      { value: "Role: Senior Software Engineer" },
      { value: "Experience: 5+ years" },
      { value: "Industry: B2B SaaS" },
      { value: "Uses CI/CD pipelines daily", primary: true },
      { value: "Based in United States" },
    ],
  },
  "market-research": {
    screeners: [
      { value: "Title: VP or Director" },
      { value: "Budget authority: $100K+" },
      { value: "Industry: Healthcare SaaS" },
      { value: "Evaluating vendors in next 6 months", primary: true },
      { value: "Company size: 500+ employees" },
    ],
  },
  "ai-training": {
    screeners: [
      { value: "Education: PhD or MD" },
      { value: "Domain: Life Sciences" },
      { value: "10+ years clinical experience" },
      { value: "Published peer-reviewed research", primary: true },
      { value: "Available for ongoing work" },
    ],
  },
};

function TabContent({ tabId }: { tabId: TabId }) {
  const { screeners } = tabScreeners[tabId];

  return (
    <div className="relative min-h-[450px] overflow-visible p-10 pb-16">
      {/* Left side - filters/screeners */}
      <div className="flex w-[364px] flex-col gap-5">
        {/* Toggle - New Recruit / Saved Panel */}
        <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start">
          <div className="bg-marketing-border-light col-start-1 row-start-1 flex w-[236px] items-center justify-end rounded-[10px] py-2.5 pr-5 pl-2.5">
            <span className="text-marketing-text text-center text-sm font-medium">
              Saved Panel
            </span>
          </div>
          <div className="bg-marketing-cream col-start-1 row-start-1 mt-0.5 ml-0.5 flex w-[113px] items-center justify-center rounded-lg px-2.5 py-2">
            <span className="text-marketing-text text-center text-sm font-medium">
              New Recruit
            </span>
          </div>
        </div>

        {/* Screener tags */}
        <div className="flex flex-col items-start gap-5">
          {screeners.map((screener) => (
            <ScreenerTag
              key={screener.value}
              value={screener.value}
              primary={screener.primary}
            />
          ))}
        </div>
      </div>

      {/* Right side - single professional headshot */}
      <div
        className="absolute overflow-visible"
        style={{
          right: 7,
          top: 25,
        }}
      >
        <SingleHeadshot tabId={tabId} />
      </div>
    </div>
  );
}

function ScreenerTag({ value, primary }: { value: string; primary?: boolean }) {
  if (primary) {
    return (
      <div className="border-marketing-border-light bg-marketing-blue flex items-center gap-2.5 rounded-lg border px-2.5 py-2">
        <span className="text-marketing-cream text-xs font-medium">
          Screener
        </span>
        <span className="text-marketing-cream text-sm font-medium">
          {value}
        </span>
      </div>
    );
  }

  return (
    <div className="border-marketing-border-light bg-marketing-cream flex items-center gap-2.5 rounded-lg border px-2.5 py-2">
      <span className="text-marketing-text-muted text-xs font-medium">
        Screener
      </span>
      <span className="text-marketing-blue text-sm font-medium">{value}</span>
    </div>
  );
}

// Single headshot per tab - matching their screener persona
const tabHeadshots: Record<
  TabId,
  { src: string; name: string; title: string }
> = {
  "user-research": {
    src: "/marketing/experts/expert-9.png",
    name: "James Miller",
    title: "Sr. Software Engineer",
  },
  "market-research": {
    src: "/marketing/experts/market-research-headshot.jpg",
    name: "David Park",
    title: "VP of Product",
  },
  "ai-training": {
    src: "/marketing/experts/ai-training-headshot.jpg",
    name: "Dr. Sarah Mitchell",
    title: "Clinical Researcher",
  },
};

function SingleHeadshot({ tabId }: { tabId: TabId }) {
  const { src, name, title } = tabHeadshots[tabId];

  return (
    <div className="relative h-[360px] w-[240px]">
      {/* Photo card - marketing-specific cream/tan design */}
      <div className="border-marketing-border bg-marketing-cream overflow-hidden rounded-[12px] border">
        {/* Photo */}
        <div className="relative h-[280px] w-full">
          <Image
            src={src}
            alt={name}
            fill
            className="object-cover"
            sizes="240px"
          />
        </div>

        {/* Info bar */}
        <div className="flex flex-col gap-0.5 px-4 py-3">
          <span className="text-marketing-text text-sm font-semibold">
            {name}
          </span>
          <span className="text-marketing-text-muted text-xs">{title}</span>
        </div>
      </div>
    </div>
  );
}
